import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { BarChart3, FileText, Users, Plus, Search, CheckCircle, Pencil, Trash2, ChevronLeft, ChevronRight, DollarSign, User, Building2, Upload, ShieldCheck, Download, Mail, Ban, CheckCircle2, Inbox, AlertTriangle, RefreshCcw, Settings, Save, FileSearch, UserPlus, MessageSquare, Eye } from 'lucide-react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import ConfirmDialog from '@/components/shared/ConfirmDialog';
import MessagesInbox from '@/components/shared/MessagesInbox';
import { toast } from 'sonner';
import { apiGet, apiPost, apiPut, apiDelete, apiBlob } from '@/lib/api';
import AuthenticatedLayout from '@/components/layout/AuthenticatedLayout';
import CompareOffers from '@/components/CompareOffers';
// The same detail view the client and the attorney see. Reused rather than
// reimplemented so an admin correcting a field edits through exactly the code
// path — and the same validation — that produced the data. It is role-aware;
// `userRole="admin"` is what unlocks editing at any status.
import { ApplicationDetailView } from '@/pages/ClientDashboard';
import { USER_TYPES, UserFormFields, UserTypePicker } from '@/components/admin/UserFormFields';
import { PageHeader, SectionCard, SummaryStatCard, EmptyState, StatusBadge, FilterToolbar, LoadingSpinner } from '@/components/shared/DashboardUI';
import { formatPhoneNumber, getCaseTypeLabel, formatDate, formatDateTime, formatRelativeTime } from '@/lib/formatters';
import '@/styles/authenticated.css';


const NAV_ITEMS = [
  { id: 'overview', label: 'Overview', icon: BarChart3 },
  { id: 'applications', label: 'Applications', icon: FileText },
  { id: 'users', label: 'Users', icon: Users },
  { id: 'law-firms', label: 'Law Firms', icon: Building2 },
  { id: 'leads', label: 'Leads', icon: UserPlus },
  { id: 'agreements', label: 'Agreements', icon: FileText },
  { id: 'messages', label: 'Messages', icon: MessageSquare },
  { id: 'email-log', label: 'Email Log', icon: Inbox },
  { id: 'platform-settings', label: 'Platform Settings', icon: Settings },
];

const AdminDashboard = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [user, setUser] = useState(null);

  const getTabFromPath = (pathname) => {
    const segment = pathname.split('/')[2] || 'overview';
    return ['overview', 'applications', 'users', 'law-firms', 'leads', 'agreements', 'messages', 'email-log', 'platform-settings'].includes(segment) ? segment : 'overview';
  };
  const [activeTab, setActiveTab] = useState(() => getTabFromPath(location.pathname));

  useEffect(() => {
    setActiveTab(getTabFromPath(location.pathname));
  }, [location.pathname]);

  useEffect(() => { fetchUser(); }, []);


  const fetchUser = async () => {
    try { setUser(await apiGet(`/users/me`)); } catch (error) { console.error('Failed:', error); }
  };

  const handleNavClick = (tab) => {
    setActiveTab(tab);
    navigate(tab === 'overview' ? '/admin' : `/admin/${tab}`);
  };

  const pageTitles = { overview: 'System Overview', applications: 'Applications', users: 'User Management', 'law-firms': 'Law Firms', leads: 'Leads', agreements: 'Agreements', messages: 'Messages', 'email-log': 'Email Log', 'platform-settings': 'Platform Settings' };

  return (
    <AuthenticatedLayout user={user} activeTab={activeTab} onTabChange={handleNavClick} navItems={NAV_ITEMS} role="admin" pageTitle={pageTitles[activeTab]}>
      {activeTab === 'overview' && <OverviewTab />}
      {activeTab === 'applications' && <ApplicationsTab currentUser={user} />}
      {activeTab === 'users' && <UsersTab />}
      {activeTab === 'law-firms' && <LawFirmsTab />}
      {activeTab === 'leads' && <LeadsTab />}
      {activeTab === 'agreements' && <AgreementsTab />}
      {activeTab === 'messages' && <MessagesInbox currentUser={user} />}
      {activeTab === 'email-log' && <EmailLogTab />}
      {activeTab === 'platform-settings' && <PlatformSettingsTab />}
    </AuthenticatedLayout>
  );
};

/* ================================================================
   Overview Tab
   ================================================================ */
const OverviewTab = () => {
  const [stats, setStats] = useState(null);


  useEffect(() => { fetchStats(); }, []);

  const fetchStats = async () => {
    try { setStats(await apiGet(`/admin/stats`)); } catch (error) { console.error('Failed:', error); }
  };

  if (!stats) return <LoadingSpinner />;

  const roleIcons = { client: User, lawyer: FileText, funder: DollarSign, admin: Users };
  const roleVariants = { client: 'primary', lawyer: 'purple', funder: 'success', admin: 'danger' };

  return (
    <div data-testid="overview-tab">
      <PageHeader title="System Overview" subtitle="Monitor platform activity and manage operations." />

      {/* User Stats */}
      <div className="mcl-stats-row">
        {stats.users_by_role?.map((item) => {
          const Icon = roleIcons[item.role] || User;
          return <SummaryStatCard key={item.role} icon={Icon} label={`${item.role}s`} value={item.count} variant={roleVariants[item.role] || 'primary'} />;
        })}
      </div>

      {/* Application and Request Stats */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: '1.5rem', marginBottom: '1.5rem' }}>
        <SectionCard title="Applications by Status">
          {stats.applications_by_status?.length > 0 ? stats.applications_by_status.map((item) => (
            <div key={item.status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid var(--mcl-border-light)' }} data-testid={`stat-apps-${item.status}`}>
              <StatusBadge status={item.status} />
              <span style={{ fontWeight: 700, color: 'var(--mcl-text-heading)', fontSize: '1.1rem' }}>{item.count}</span>
            </div>
          )) : <span style={{ color: 'var(--mcl-text-muted)', fontSize: '0.875rem' }}>No applications yet</span>}
        </SectionCard>

        <SectionCard title="Offers by Status">
          {stats.offers_by_status?.length > 0 ? stats.offers_by_status.map((item) => (
            <div key={item.status} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '0.5rem 0', borderBottom: '1px solid var(--mcl-border-light)' }} data-testid={`stat-offers-${item.status}`}>
              <StatusBadge status={item.status} />
              <span style={{ fontWeight: 700, color: 'var(--mcl-text-heading)', fontSize: '1.1rem' }}>{item.count}</span>
            </div>
          )) : <span style={{ color: 'var(--mcl-text-muted)', fontSize: '0.875rem' }}>No offers yet</span>}
        </SectionCard>
      </div>

      {/* Total Funding */}
      <div className="mcl-card" style={{ background: 'var(--mcl-sidebar-bg)', border: 'none', padding: '1.75rem 2rem', color: '#fff' }}>
        <div style={{ fontSize: '0.85rem', fontWeight: 500, opacity: 0.75, marginBottom: '0.35rem' }}>Total Funding Disbursed</div>
        <div style={{ fontSize: '2.5rem', fontWeight: 700 }}>${parseFloat(stats.total_funding || 0).toLocaleString()}</div>
      </div>
    </div>
  );
};

/* ================================================================
   Applications Tab
   ================================================================ */
export const ApplicationsTab = ({ currentUser }) => {
  const [applications, setApplications] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [offersModalApp, setOffersModalApp] = useState(null); // { application_id, offer_count, applicant_name }
  // The row list carries only the summary fields the table renders, so opening
  // an application refetches the full document — editing a partial one would
  // PUT back a form full of blanks for every field the list omits.
  const [selectedApplication, setSelectedApplication] = useState(null);
  const [openingId, setOpeningId] = useState(null);

  const getAuthHeaders = () => { const token = localStorage.getItem('access_token'); return token ? { 'Authorization': `Bearer ${token}` } : {}; };

  useEffect(() => { fetchApplications(); }, []);

  const fetchApplications = async () => {
    try { setApplications(await apiGet(`/applications`)); } catch (error) { console.error('Failed:', error); }
  };

  const filtered = applications.filter(app =>
    (app.case_type || app.caseType || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (app.firstName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (app.lastName || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
    (app.application_id || '').toLowerCase().includes(searchTerm.toLowerCase())
  );

  const handleViewApplication = async (appId) => {
    setOpeningId(appId);
    try {
      setSelectedApplication(await apiGet(`/applications/${appId}`));
    } catch (e) {
      console.error('Failed to open application:', e);
      toast.error('Failed to open application');
    } finally {
      setOpeningId(null);
    }
  };

  const handleDownloadPdf = async (appId) => {
    try {
      const blob = await apiBlob(`/applications/${appId}/underwriting-pdf`);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `mycaseloan_application_${appId}_underwriting.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      toast.success('PDF downloaded');
    } catch (e) {
      console.error('PDF download error:', e);
      toast.error('Failed to download PDF');
    }
  };

  if (selectedApplication) {
    return (
      <ApplicationDetailView
        application={selectedApplication}
        onBack={() => setSelectedApplication(null)}
        // Refresh the table so the row behind the editor reflects the save.
        // Deliberately not refetching the detail as well: ApplicationDetailView
        // seeds `formData` once and never re-syncs from the prop, so a second
        // fetch would update nothing an admin can see.
        onUpdate={fetchApplications}
        userRole="admin"
        canCancel={false}
        currentUser={currentUser}
      />
    );
  }

  return (
    <div data-testid="applications-tab">
      <PageHeader title="All Applications" subtitle="View and manage all funding applications." />
      <FilterToolbar searchValue={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search by case type, name, or ID..." />

      <SectionCard title={`Applications (${filtered.length})`}>
        {filtered.length === 0 ? (
          <EmptyState icon={FileText} title="No applications found" text="No matching applications." />
        ) : (
          <div className="mcl-table-wrapper">
            <table className="mcl-table">
              <thead><tr><th>Applicant</th><th>Case Type</th><th>Amount</th><th>Lawyer / Firm</th><th>Funder</th><th>Status</th><th>Offers</th><th>Created</th><th style={{ textAlign: 'right' }}>Actions</th></tr></thead>
              <tbody>
                {filtered.map((app) => (
                  <tr key={app.application_id} data-testid={`application-${app.application_id}`}>
                    <td>
                      <div style={{ fontWeight: 600, color: 'var(--mcl-text-heading)' }}>{app.firstName && app.lastName ? `${app.firstName} ${app.lastName}` : 'Client'}</div>
                      <div style={{ fontSize: '0.75rem', color: 'var(--mcl-text-light)' }}>ID: {app.application_id?.slice(0, 12)}...</div>
                    </td>
                    <td>{getCaseTypeLabel(app.caseType || app.case_type)}</td>
                    <td style={{ fontWeight: 600 }}>{(app.requestedAmount || app.requested_amount) ? `$${parseFloat(app.requestedAmount || app.requested_amount).toLocaleString()}` : '-'}</td>
                    <td>
                      <div>{app.lawyer_name || '-'}</div>
                      {app.lawFirmName && <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{app.lawFirmName}</div>}
                    </td>
                    <td data-testid={`funder-cell-${app.application_id}`}>
                      {app.status === 'funded' && (app.funder_company_name || app.funder_name) ? (
                        <>
                          <div style={{ fontWeight: 600 }}>{app.funder_company_name || app.funder_name}</div>
                          {app.funder_company_name && app.funder_name && (
                            <div style={{ fontSize: '0.78rem', color: 'var(--mcl-text-muted)' }}>{app.funder_name}</div>
                          )}
                          {app.funded_amount !== undefined && app.funded_amount !== null && (
                            <div style={{ fontSize: '0.78rem', color: 'var(--mcl-success, #16a34a)', fontWeight: 600 }}>${parseFloat(app.funded_amount).toLocaleString()} advanced</div>
                          )}
                        </>
                      ) : (
                        <span style={{ color: 'var(--mcl-text-light)' }}>-</span>
                      )}
                    </td>
                    <td><StatusBadge status={app.status} /></td>
                    <td data-testid={`offers-cell-${app.application_id}`}>
                      {(app.offer_count || 0) > 0 ? (
                        <button
                          type="button"
                          className="mcl-btn mcl-btn-sm mcl-btn-outline"
                          onClick={() => setOffersModalApp({
                            application_id: app.application_id,
                            offer_count: app.offer_count,
                            applicant_name: app.firstName && app.lastName ? `${app.firstName} ${app.lastName}` : 'Client',
                          })}
                          data-testid={`view-offers-${app.application_id}`}
                          style={{ display: 'inline-flex', alignItems: 'center', gap: '0.35rem', padding: '0.2rem 0.55rem', fontSize: '0.78rem' }}
                          title={`View ${app.offer_count} offer${app.offer_count === 1 ? '' : 's'}`}
                        >
                          <FileSearch size={13} /> {app.offer_count} {app.offer_count === 1 ? 'offer' : 'offers'}
                        </button>
                      ) : (
                        <span style={{ color: 'var(--mcl-text-light)', fontSize: '0.85rem' }}>—</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{formatDate(app.created_at)}</td>
                    <td style={{ textAlign: 'right' }}>
                      <div style={{ display: 'inline-flex', gap: '0.4rem', justifyContent: 'flex-end' }}>
                        <button
                          className="mcl-btn mcl-btn-sm mcl-btn-outline-primary"
                          onClick={() => handleViewApplication(app.application_id)}
                          disabled={openingId === app.application_id}
                          data-testid={`view-application-${app.application_id}`}
                          title="View and edit this application"
                        >
                          <Eye size={13} /> {openingId === app.application_id ? 'Opening…' : 'View'}
                        </button>
                        <button
                          className="mcl-btn mcl-btn-sm mcl-btn-outline"
                          onClick={() => handleDownloadPdf(app.application_id)}
                          data-testid={`download-pdf-${app.application_id}`}
                          title="Download Underwriting PDF"
                        >
                          <Download size={13} /> PDF
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Offers Preview Modal */}
      <Dialog open={!!offersModalApp} onOpenChange={(open) => { if (!open) setOffersModalApp(null); }}>
        <DialogContent style={{ maxWidth: '880px', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }} data-testid="admin-offers-modal">
          <DialogHeader>
            <DialogTitle>
              Offers on Application
              {offersModalApp && (
                <span style={{ marginLeft: '0.6rem', color: 'var(--mcl-text-muted)', fontWeight: 400, fontSize: '0.9rem' }}>
                  · {offersModalApp.applicant_name} · {offersModalApp.application_id?.slice(0, 12)}…
                </span>
              )}
            </DialogTitle>
            <DialogDescription>
              {offersModalApp ? `${offersModalApp.offer_count} funder${offersModalApp.offer_count === 1 ? '' : 's'} have submitted offers (latest per funder shown).` : ''}
            </DialogDescription>
          </DialogHeader>
          <div style={{ overflowY: 'auto', flex: 1, paddingRight: '0.25rem' }}>
            {offersModalApp && (
              <CompareOffers
                applicationId={offersModalApp.application_id}
                canAccept={false}
                showRisk={true}
              />
            )}
          </div>
          <DialogFooter style={{ paddingTop: '0.6rem', borderTop: '1px solid var(--mcl-border-light, #e2e8f0)' }}>
            <button type="button" className="mcl-btn mcl-btn-outline" onClick={() => setOffersModalApp(null)} data-testid="admin-offers-modal-close">Close</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

/* ================================================================
   Notify Funders Modal
   ================================================================ */
const NotifyFundersModal = ({ onClose, funders, loading, selected, setSelected, message, setMessage, leadCount, setLeadCount, sending, onLoad, onSend }) => {
  const [search, setSearch] = useState('');

  // Fetch funders once on mount
  useEffect(() => {
    onLoad();
  }, []);

  const filtered = funders.filter(f => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (f.name || '').toLowerCase().includes(s)
      || (f.email || '').toLowerCase().includes(s)
      || (f.company_name || '').toLowerCase().includes(s);
  });

  const selectedCount = Object.values(selected).filter(Boolean).length;
  const toggleOne = (id) => setSelected(prev => ({ ...prev, [id]: !prev[id] }));
  const selectAllVisible = () => {
    const next = { ...selected };
    filtered.forEach(f => { next[f.user_id] = true; });
    setSelected(next);
  };
  const clearAll = () => setSelected({});

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent style={{ maxWidth: '640px', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }} data-testid="notify-funders-modal">
        <DialogHeader>
          <DialogTitle>Notify Funders of New Leads</DialogTitle>
          <DialogDescription>Send an email blast to selected funders informing them of new lead(s) available.</DialogDescription>
        </DialogHeader>

        <div style={{ overflowY: 'auto', flex: 1, paddingRight: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <div className="mcl-form-row" style={{ gap: '0.625rem' }}>
            <div className="mcl-form-group" style={{ marginBottom: 0 }}>
              <label className="mcl-form-label">Lead Count (optional)</label>
              <input type="number" min="0" className="mcl-form-input" value={leadCount} onChange={(e) => setLeadCount(e.target.value)} placeholder="e.g. 5" data-testid="notify-lead-count" />
            </div>
          </div>

          <div className="mcl-form-group" style={{ marginBottom: 0 }}>
            <label className="mcl-form-label">Custom Message (optional)</label>
            <textarea className="mcl-form-input" rows={3} value={message} onChange={(e) => setMessage(e.target.value)} placeholder="Add a personal note (will appear in the email body)..." data-testid="notify-custom-message" style={{ resize: 'vertical' }} />
          </div>

          <div style={{ borderTop: '1px solid var(--mcl-border-light, #e2e8f0)', paddingTop: '0.75rem' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem', gap: '0.5rem', flexWrap: 'wrap' }}>
              <strong style={{ fontSize: '0.9rem' }}>Select Funders ({selectedCount} selected)</strong>
              <div style={{ display: 'flex', gap: '0.35rem' }}>
                <button type="button" className="mcl-btn mcl-btn-sm mcl-btn-outline" onClick={selectAllVisible} data-testid="notify-select-all" disabled={filtered.length === 0}>Select All</button>
                <button type="button" className="mcl-btn mcl-btn-sm mcl-btn-outline" onClick={clearAll} data-testid="notify-clear-all" disabled={selectedCount === 0}>Clear</button>
              </div>
            </div>
            <div style={{ position: 'relative', marginBottom: '0.5rem' }}>
              <Search size={14} style={{ position: 'absolute', left: '0.65rem', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-light)' }} />
              <input className="mcl-form-input" placeholder="Search funders..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="notify-funder-search" style={{ paddingLeft: '2rem' }} />
            </div>

            {loading ? (
              <LoadingSpinner />
            ) : filtered.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--mcl-text-muted)', fontSize: '0.85rem', border: '1px dashed var(--mcl-border-light, #e2e8f0)', borderRadius: '8px' }}>
                {funders.length === 0 ? 'No funders found' : 'No funders match your search'}
              </div>
            ) : (
              <div style={{ maxHeight: '260px', overflowY: 'auto', border: '1px solid var(--mcl-border-light, #e2e8f0)', borderRadius: '8px' }}>
                {filtered.map(f => (
                  <label key={f.user_id} htmlFor={`notify-funder-${f.user_id}`} style={{ display: 'flex', alignItems: 'center', gap: '0.6rem', padding: '0.55rem 0.75rem', borderBottom: '1px solid var(--mcl-border-light, #f1f5f9)', cursor: 'pointer', background: selected[f.user_id] ? '#eff6ff' : 'transparent' }} data-testid={`notify-funder-row-${f.user_id}`}>
                    <input
                      id={`notify-funder-${f.user_id}`}
                      type="checkbox"
                      checked={!!selected[f.user_id]}
                      onChange={() => toggleOne(f.user_id)}
                      data-testid={`notify-funder-checkbox-${f.user_id}`}
                    />
                    <div style={{ flex: 1, minWidth: 0 }}>
                      <div style={{ fontWeight: 600, fontSize: '0.88rem', color: 'var(--mcl-text-heading)' }}>{f.name || 'Unnamed Funder'}{f.company_name ? <span style={{ color: 'var(--mcl-text-muted)', fontWeight: 400 }}> · {f.company_name}</span> : null}</div>
                      <div style={{ fontSize: '0.78rem', color: 'var(--mcl-text-muted)' }}>{f.email}</div>
                    </div>
                  </label>
                ))}
              </div>
            )}
          </div>
        </div>

        <DialogFooter style={{ paddingTop: '0.75rem', borderTop: '1px solid var(--mcl-border-light, #e2e8f0)' }}>
          <button type="button" className="mcl-btn mcl-btn-outline" onClick={onClose} disabled={sending} data-testid="notify-cancel-btn">Cancel</button>
          <button type="button" className="mcl-btn mcl-btn-primary" onClick={onSend} disabled={sending || selectedCount === 0} data-testid="notify-send-btn">
            {sending ? 'Sending...' : `Send to ${selectedCount} Funder${selectedCount === 1 ? '' : 's'}`}
          </button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ================================================================
   Growth Partner Assignments Modal
   ================================================================ */
/**
 * Assignment rows are append-only provenance for later revenue logic — this
 * modal never deletes one. "Assign" opens a new row (closing whichever one
 * currently has the lawyer, server-side); "End" closes the active row and
 * keeps it, so history renders alongside the active lawyers rather than
 * hiding it.
 */
export const GrowthPartnerAssignmentsModal = ({ partner, onClose }) => {
  const [assignments, setAssignments] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [lawyers, setLawyers] = useState([]);
  const [lawyersLoading, setLawyersLoading] = useState(false);
  const [selectedLawyerId, setSelectedLawyerId] = useState('');
  const [reason, setReason] = useState('');
  const [assigning, setAssigning] = useState(false);
  const [endingLawyerId, setEndingLawyerId] = useState(null);
  const [campaigns, setCampaigns] = useState([]);
  const [campaignsTotal, setCampaignsTotal] = useState(0);
  const [campaignsLoading, setCampaignsLoading] = useState(false);

  const loadAssignments = async () => {
    setLoading(true);
    try {
      const data = await apiGet(`/admin/growth-partners/${partner.user_id}/lawyers`);
      setAssignments(data.assignments || []);
      setTotal(data.total ?? (data.assignments || []).length);
    } catch (error) { toast.error(error.message || 'Failed to load assignments'); }
    finally { setLoading(false); }
  };

  const loadCampaigns = async () => {
    setCampaignsLoading(true);
    try {
      const data = await apiGet(`/admin/growth-partners/${partner.user_id}/campaigns`);
      setCampaigns(data.campaigns || []);
      setCampaignsTotal(data.total ?? (data.campaigns || []).length);
    } catch (error) { toast.error(error.message || 'Failed to load campaigns'); }
    finally { setCampaignsLoading(false); }
  };

  const loadLawyers = async () => {
    setLawyersLoading(true);
    try {
      const data = await apiGet('/admin/users?role_filter=lawyer&limit=200');
      setLawyers((data.users || []).filter((u) => !u.disabled));
    } catch (error) { toast.error(error.message || 'Failed to load lawyers'); }
    finally { setLawyersLoading(false); }
  };

  // Fetch-on-mount only, matching NotifyFundersModal's onLoad effect above —
  // loadAssignments/loadLawyers are re-created every render, so listing them
  // as deps would refetch on every keystroke in the form below instead of once.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { loadAssignments(); loadLawyers(); loadCampaigns(); }, []);

  const handleAssign = async () => {
    if (!selectedLawyerId) return;
    setAssigning(true);
    try {
      const payload = { lawyer_id: selectedLawyerId };
      if (reason.trim()) payload.reason = reason.trim();
      await apiPost(`/admin/growth-partners/${partner.user_id}/lawyers`, payload);
      toast.success('Lawyer assigned');
      setSelectedLawyerId(''); setReason('');
      await loadAssignments();
    } catch (error) { toast.error(error.message || 'Failed to assign lawyer'); }
    finally { setAssigning(false); }
  };

  const handleEnd = async (lawyerId) => {
    if (!window.confirm('End this assignment? The lawyer will no longer be attributed to this partner.')) return;
    setEndingLawyerId(lawyerId);
    try {
      await apiDelete(`/admin/growth-partners/${partner.user_id}/lawyers/${lawyerId}`);
      toast.success('Assignment ended');
      await loadAssignments();
    } catch (error) { toast.error(error.message || 'Failed to end assignment'); }
    finally { setEndingLawyerId(null); }
  };

  return (
    <Dialog open={true} onOpenChange={(open) => { if (!open) onClose(); }}>
      <DialogContent style={{ maxWidth: '680px', maxHeight: '88vh', display: 'flex', flexDirection: 'column' }} data-testid="gp-assignment-modal">
        <DialogHeader>
          <DialogTitle>Lawyer Assignments</DialogTitle>
          <DialogDescription>{partner.name || partner.email} refers these lawyers to the platform.</DialogDescription>
        </DialogHeader>

        <div style={{ overflowY: 'auto', flex: 1, paddingRight: '0.5rem', display: 'flex', flexDirection: 'column', gap: '0.85rem' }}>
          <div style={{ display: 'flex', gap: '0.625rem', flexWrap: 'wrap', alignItems: 'flex-end' }}>
            <div className="mcl-form-group" style={{ marginBottom: 0, flex: 1, minWidth: '200px' }}>
              <label className="mcl-form-label">Assign a lawyer</label>
              <select
                className="mcl-form-input"
                value={selectedLawyerId}
                onChange={(e) => setSelectedLawyerId(e.target.value)}
                disabled={lawyersLoading}
                data-testid="gp-assign-lawyer-select"
              >
                <option value="">{lawyersLoading ? 'Loading lawyers…' : 'Select a lawyer'}</option>
                {lawyers.map((l) => (
                  <option key={l.user_id} value={l.user_id}>
                    {l.name || l.email}{l.company_name ? ` · ${l.company_name}` : ''}
                  </option>
                ))}
              </select>
            </div>
            <div className="mcl-form-group" style={{ marginBottom: 0, flex: 1, minWidth: '200px' }}>
              <label className="mcl-form-label">Reason (optional)</label>
              <input
                className="mcl-form-input"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                placeholder="e.g. lawyer's own request"
                data-testid="gp-assign-reason"
              />
            </div>
            <button
              type="button"
              className="mcl-btn mcl-btn-primary"
              onClick={handleAssign}
              disabled={assigning || !selectedLawyerId}
              data-testid="gp-assign-submit"
            >
              {assigning ? 'Assigning…' : 'Assign'}
            </button>
          </div>

          <div style={{ borderTop: '1px solid var(--mcl-border-light, #e2e8f0)', paddingTop: '0.75rem' }}>
            <strong style={{ fontSize: '0.9rem' }}>
              Assignments{total > assignments.length ? ` (showing ${assignments.length} most recent of ${total})` : ` (${total})`}
            </strong>
            {loading ? (
              <LoadingSpinner />
            ) : assignments.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--mcl-text-muted)', fontSize: '0.85rem', border: '1px dashed var(--mcl-border-light, #e2e8f0)', borderRadius: '8px', marginTop: '0.5rem' }} data-testid="gp-assignments-empty">
                No lawyers have been assigned yet.
              </div>
            ) : (
              <div className="mcl-table-wrapper" style={{ marginTop: '0.5rem' }}>
                <table className="mcl-table">
                  <thead><tr><th>Lawyer</th><th>Status</th><th>Assigned</th><th>Ended</th><th>Method</th><th>Referral code</th><th>Clicks</th><th></th></tr></thead>
                  <tbody>
                    {assignments.map((a) => (
                      <tr key={a.assignment_id} data-testid={`gp-assignment-${a.assignment_id}`}>
                        <td>
                          <div style={{ fontWeight: 600 }}>{a.lawyer?.name || a.lawyer_id}</div>
                          {a.lawyer?.company_name && (
                            <div style={{ fontSize: '0.78rem', color: 'var(--mcl-text-muted)' }}>{a.lawyer.company_name}</div>
                          )}
                        </td>
                        <td>
                          {a.active
                            ? <span className="mcl-badge mcl-badge-success">Active</span>
                            : <span className="mcl-badge mcl-badge-secondary">Ended</span>}
                        </td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{formatDate(a.assigned_at)}</td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{a.ended_at ? formatDate(a.ended_at) : '—'}</td>
                        <td data-testid={`gp-assignment-method-${a.assignment_id}`} style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>
                          {a.attribution_method === 'referral_link' ? 'Referral link' : 'Admin'}
                        </td>
                        <td data-testid={`gp-assignment-code-${a.assignment_id}`} style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>
                          {a.referral_code || '—'}
                        </td>
                        <td data-testid={`gp-assignment-clicks-${a.assignment_id}`} style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>
                          {a.first_click_at
                            ? `${new Date(a.first_click_at).toLocaleDateString()} → ${new Date(a.last_click_at).toLocaleDateString()}`
                            : '—'}
                        </td>
                        <td>
                          {a.active && (
                            <button
                              type="button"
                              className="mcl-btn mcl-btn-sm mcl-btn-outline"
                              onClick={() => handleEnd(a.lawyer_id)}
                              disabled={endingLawyerId === a.lawyer_id}
                              data-testid={`gp-end-assignment-${a.assignment_id}`}
                            >
                              {endingLawyerId === a.lawyer_id ? 'Ending…' : 'End'}
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div style={{ borderTop: '1px solid var(--mcl-border-light, #e2e8f0)', paddingTop: '0.75rem' }}>
            <strong style={{ fontSize: '0.9rem' }}>Campaigns ({campaignsTotal})</strong>
            {campaignsLoading ? (
              <LoadingSpinner />
            ) : campaigns.length === 0 ? (
              <div style={{ padding: '1rem', textAlign: 'center', color: 'var(--mcl-text-muted)', fontSize: '0.85rem', border: '1px dashed var(--mcl-border-light, #e2e8f0)', borderRadius: '8px', marginTop: '0.5rem' }} data-testid="gp-campaigns-empty">
                This partner has not created any campaigns yet.
              </div>
            ) : (
              <div className="mcl-table-wrapper" style={{ marginTop: '0.5rem' }}>
                <table className="mcl-table">
                  <thead><tr><th>Name</th><th>Code</th><th>Destination</th><th>Status</th><th>Clicks</th><th>Signups</th><th>Created</th></tr></thead>
                  <tbody>
                    {campaigns.map((c) => (
                      <tr key={c.campaign_id} data-testid={`gp-campaign-${c.campaign_id}`}>
                        <td>{c.name}</td>
                        <td data-testid={`gp-campaign-code-${c.campaign_id}`} style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{c.campaign_code}</td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{c.destination_path}</td>
                        <td>
                          {c.active
                            ? <span className="mcl-badge mcl-badge-success">Active</span>
                            : <span className="mcl-badge mcl-badge-secondary">Inactive</span>}
                        </td>
                        <td data-testid={`gp-campaign-clicks-${c.campaign_id}`} style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{c.clicks}</td>
                        <td data-testid={`gp-campaign-signups-${c.campaign_id}`} style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{c.signups}</td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{formatDate(c.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <DialogFooter style={{ paddingTop: '0.6rem', borderTop: '1px solid var(--mcl-border-light, #e2e8f0)' }}>
          <button type="button" className="mcl-btn mcl-btn-outline" onClick={onClose} data-testid="gp-assignment-modal-close">Close</button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ================================================================
   Users Tab
   ================================================================ */
export const UsersTab = () => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [pagination, setPagination] = useState({ page: 1, limit: 10, total: 0, total_pages: 0, has_next: false, has_prev: false });
  const [loading, setLoading] = useState(false);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPassword, setShowPassword] = useState(false);
  const [formErrors, setFormErrors] = useState({});

  // Fee fields are funder-only and are written by a second call after create —
  // POST /admin/users does not accept them. Defaults match what the backend
  // seeds for a new funder: 15%, no cap, platform-default application fee.
  const initialFormData = { email: '', name: '', role: 'client', company_name: '', firm_id: '', new_firm_name: '', phone: '', address1: '', address2: '', city: '', state: '', zip: '', broker_fee_percentage: '15', broker_fee_cap: '', application_fee: '', revenue_share_percentage: '' };
  const [formData, setFormData] = useState(initialFormData);
  const [submitting, setSubmitting] = useState(false);
  const [firmSearch, setFirmSearch] = useState('');
  const [firmOptions, setFirmOptions] = useState([]);
  const [firmMode, setFirmMode] = useState('select'); // 'select' or 'other'
  // Notify-Funders blast modal
  const [showNotifyFundersModal, setShowNotifyFundersModal] = useState(false);
  const [notifyFunders, setNotifyFunders] = useState([]);
  const [notifyLoading, setNotifyLoading] = useState(false);
  const [notifySelected, setNotifySelected] = useState({});
  const [notifyMessage, setNotifyMessage] = useState('');
  const [notifyLeadCount, setNotifyLeadCount] = useState('');
  const [notifySending, setNotifySending] = useState(false);
  // Growth Partner assignment modal — the user it's open for, or null when closed.
  const [assignmentsPartner, setAssignmentsPartner] = useState(null);


  useEffect(() => { fetchUsers(); }, [pagination.page, roleFilter]);

  // The search debounce must not run on mount. Both effects fire on the first
  // render, so without this guard every visit to the tab issued the same
  // /admin/users request twice — once immediately, once 300ms later.
  const searchDebounced = useRef(false);
  useEffect(() => {
    if (!searchDebounced.current) { searchDebounced.current = true; return; }
    const delay = setTimeout(() => { if (pagination.page === 1) fetchUsers(); else setPagination(prev => ({ ...prev, page: 1 })); }, 300);
    return () => clearTimeout(delay);
  }, [searchTerm]);

  const fetchUsers = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: pagination.page.toString(), limit: pagination.limit.toString() });
      if (searchTerm) params.append('search', searchTerm);
      if (roleFilter !== 'all') params.append('role_filter', roleFilter);
      const data = await apiGet(`/admin/users?${params}`);
      setUsers(data.users); setPagination(prev => ({ ...prev, ...data.pagination }));
    } catch (error) { toast.error(error.message || 'Failed to fetch users'); }
    finally { setLoading(false); }
  };

  const resetForm = () => { setFormData(initialFormData); setFormErrors({}); setShowPassword(false); setFirmSearch(''); setFirmOptions([]); setFirmMode('select'); setSubmitting(false); };

  const searchFirms = async (q) => {
    setFirmSearch(q);
    if (q.length < 2) { setFirmOptions([]); return; }
    try {
      const data = await apiGet(`/law-firms/search?q=${encodeURIComponent(q)}&limit=10`);
      setFirmOptions(data.firms || []);
    } catch (error) { console.error('Firm search failed:', error); }
  };

  const validateForm = () => {
    const errors = {};
    if (!formData.email) errors.email = 'Email is required';
    if (formData.email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) errors.email = 'Invalid email';
    if (!formData.name) errors.name = 'Name is required';
    if (!formData.role) errors.role = 'Role is required';
    if (formData.role === 'growth_partner' && formData.revenue_share_percentage === '') {
      errors.revenue_share_percentage = 'Revenue share is required';
    }
    setFormErrors(errors);
    return Object.keys(errors).length === 0;
  };

  /** Fee overrides as the broker-fee endpoint wants them: numbers, or null for "use the default". */
  const feePayload = () => {
    const optional = (v) => (v === '' || v === null || v === undefined ? null : parseFloat(v));
    return {
      broker_fee_percentage: parseFloat(formData.broker_fee_percentage),
      broker_fee_cap: optional(formData.broker_fee_cap),
      application_fee: optional(formData.application_fee),
    };
  };

  const handleCreateUser = async (e) => {
    if (e) e.preventDefault();
    if (submitting) return;
    if (!validateForm()) return;

    setSubmitting(true);
    try {
      const { broker_fee_percentage, broker_fee_cap, application_fee, revenue_share_percentage, ...payload } = formData;
      if (formData.role === 'lawyer' && firmMode === 'other') {
        payload.new_firm_name = formData.new_firm_name;
        payload.firm_id = '';
      } else if (formData.role === 'lawyer' && firmMode === 'select' && formData.firm_id) {
        payload.new_firm_name = '';
      }
      // Unlike the funder fees, this rides on the initial create call itself —
      // POST /admin/users 400s a growth_partner with none, because the
      // referral code and share are assigned together at creation.
      if (formData.role === 'growth_partner') {
        payload.revenue_share_percentage = parseFloat(revenue_share_percentage);
      }

      const created = await apiPost(`/admin/users`, payload);

      // Fees ride on a second call. If it fails the user still exists, so say
      // that rather than reporting the whole thing as a failure.
      if (formData.role === 'funder') {
        const userId = created?.user?.user_id;
        try {
          if (userId) await apiPut(`/admin/users/${userId}/broker-fee`, feePayload());
        } catch (feeError) {
          toast.error(`User created, but the fee overrides did not save: ${feeError.message || 'unknown error'}. Set them from the user's detail page.`);
          setIsCreateModalOpen(false); resetForm(); fetchUsers();
          return;
        }
      }

      toast.success('User created. Setup email sent.'); setIsCreateModalOpen(false); resetForm(); fetchUsers();
    } catch (error) { toast.error(error.message || 'Failed to create user'); }
    finally { setSubmitting(false); }
  };

  const handleDeleteUser = async () => {
    try {
      await apiDelete(`/admin/users/${selectedUser.user_id}`);
      toast.success('User deleted'); setIsDeleteModalOpen(false); setSelectedUser(null); fetchUsers();
    } catch (error) { toast.error(error.message || 'Failed to delete'); }
  };

  const handleApproveFunder = async (userId) => {
    try {
      await apiPost(`/admin/users/${userId}/approve-funder`);
      toast.success('Funder approved! Notification email sent.'); fetchUsers();
    } catch (error) { toast.error(error.message || 'Failed to approve funder'); }
  };

  const handleApproveLawyer = async (userId) => {
    try {
      await apiPost(`/admin/users/${userId}/approve-lawyer`);
      toast.success('Lawyer approved! Notification email sent.'); fetchUsers();
    } catch (error) { toast.error(error.message || 'Failed to approve lawyer'); }
  };

  const handleResendApproval = async (userId, email) => {
    try {
      await apiPost(`/admin/users/${userId}/resend-approval-email`);
      toast.success(`Approval email resent to ${email}`);
    } catch (error) { toast.error(error.message || 'Failed to resend approval email'); }
  };

  const handleToggleDisabled = async (user) => {
    const nextDisabled = !user.disabled;
    const verb = nextDisabled ? 'disable' : 're-enable';
    if (nextDisabled && !window.confirm(`Disable ${user.name || user.email}? They will be logged out everywhere and blocked from logging in until re-enabled.`)) return;
    try {
      await apiPut(`/admin/users/${user.user_id}/disable`, { disabled: nextDisabled });
      toast.success(nextDisabled ? `${user.name || user.email} disabled` : `${user.name || user.email} re-enabled`);
      fetchUsers();
    } catch (error) {
      toast.error(error.message || `Failed to ${verb} account`);
    }
  };

  const getRoleBadgeVariant = (role) => ({ client: 'primary', lawyer: 'purple', funder: 'success', admin: 'danger', growth_partner: 'info' }[role] || 'secondary');
  // growth_partner has no clean capitalize()-only rendering — underscores don't
  // become spaces — so every role name is looked up rather than special-cased.
  const getRoleLabel = (role) => USER_TYPES.find((t) => t.value === role)?.label || role;

  return (
    <div data-testid="users-tab">
      <PageHeader title="All Users" subtitle="Manage platform users and their roles." actions={
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="mcl-btn mcl-btn-outline" onClick={() => setShowNotifyFundersModal(true)} data-testid="notify-funders-btn"><Mail size={16} /> Notify Funders</button>
          <button className="mcl-btn mcl-btn-primary" onClick={() => { resetForm(); setIsCreateModalOpen(true); }} data-testid="create-user-btn"><Plus size={16} /> Add User</button>
        </div>
      } />

      <FilterToolbar searchValue={searchTerm} onSearchChange={setSearchTerm} searchPlaceholder="Search by name, email, or company...">
        <Select value={roleFilter} onValueChange={(value) => { setRoleFilter(value); setPagination(prev => ({ ...prev, page: 1 })); }}>
          <SelectTrigger style={{ width: '160px' }} data-testid="role-filter-select"><SelectValue placeholder="Filter by role" /></SelectTrigger>
          <SelectContent><SelectItem value="all">All Roles</SelectItem><SelectItem value="client">Client</SelectItem><SelectItem value="lawyer">Lawyer</SelectItem><SelectItem value="funder">Funder</SelectItem><SelectItem value="admin">Admin</SelectItem><SelectItem value="growth_partner">Growth Partner</SelectItem></SelectContent>
        </Select>
      </FilterToolbar>

      <SectionCard title={`Users (${pagination.total})`}>
        {loading ? <LoadingSpinner /> : users.length === 0 ? (
          <EmptyState icon={Users} title="No users found" text="No matching users." />
        ) : (
          <div className="mcl-table-wrapper">
            <table className="mcl-table">
              <thead><tr><th>User</th><th>Role</th>{roleFilter === 'growth_partner' ? (
                <><th>Referral Code</th><th>Revenue Share</th><th>Assigned Lawyers</th></>
              ) : (
                <><th>Company</th><th>Phone</th></>
              )}<th>Status</th><th>Joined</th><th>Last Login</th><th>Actions</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.user_id} data-testid={`user-${u.user_id}`} style={u.disabled ? { background: '#fef2f2' } : undefined}>
                    <td>
                      <div style={{ fontWeight: 600, color: u.disabled ? 'var(--mcl-text-muted)' : 'var(--mcl-text-heading)', textDecoration: u.disabled ? 'line-through' : 'none' }}>{u.name || 'Unnamed User'}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{u.email}</div>
                    </td>
                    <td><span className={`mcl-badge mcl-badge-${getRoleBadgeVariant(u.role)}`} data-testid={`role-${u.role}`}>{getRoleLabel(u.role)}</span></td>
                    {roleFilter === 'growth_partner' ? (
                      <>
                        <td style={{ fontFamily: 'monospace', fontWeight: 600 }} data-testid={`gp-referral-code-${u.user_id}`}>
                          {u.referral_code || <span style={{ color: 'var(--mcl-text-light)' }}>-</span>}
                        </td>
                        <td data-testid={`gp-revenue-share-${u.user_id}`}>
                          {u.revenue_share_percentage != null ? `${u.revenue_share_percentage}%` : <span style={{ color: 'var(--mcl-text-light)' }}>-</span>}
                        </td>
                        <td data-testid={`gp-assigned-count-${u.user_id}`}>{u.assigned_lawyer_count ?? 0}</td>
                      </>
                    ) : (
                      <>
                        <td>{u.company_name || <span style={{ color: 'var(--mcl-text-light)' }}>-</span>}</td>
                        <td style={{ fontSize: '0.85rem' }}>{u.phone ? formatPhoneNumber(u.phone) : <span style={{ color: 'var(--mcl-text-light)' }}>-</span>}</td>
                      </>
                    )}
                    <td>
                      {u.disabled ? (
                        <span className="mcl-badge mcl-badge-danger" style={{ fontSize: '0.68rem' }} data-testid={`disabled-badge-${u.user_id}`}><Ban size={10} style={{ marginRight: '2px' }} /> Disabled</span>
                      ) : u.role === 'funder' && u.funder_approved === false ? (
                        <span className="mcl-badge mcl-badge-warning" style={{ fontSize: '0.68rem' }} data-testid={`funder-pending-${u.user_id}`}>Pending Approval</span>
                      ) : u.role === 'lawyer' && u.lawyer_approved === false ? (
                        <span className="mcl-badge mcl-badge-warning" style={{ fontSize: '0.68rem' }} data-testid={`lawyer-pending-${u.user_id}`}>Pending Approval</span>
                      ) : u.email_verified ? (
                        <span className="mcl-badge mcl-badge-success" style={{ fontSize: '0.68rem' }}><CheckCircle size={10} style={{ marginRight: '2px' }} /> Verified</span>
                      ) : (
                        <span style={{ color: 'var(--mcl-text-light)', fontSize: '0.8rem' }}>Unverified</span>
                      )}
                    </td>
                    <td style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{formatDate(u.created_at)}</td>
                    <td style={{ fontSize: '0.8rem', color: u.last_login_at ? 'var(--mcl-text-muted)' : 'var(--mcl-text-light)' }} data-testid={`last-login-${u.user_id}`} title={u.last_login_at ? formatDateTime(u.last_login_at) : 'Never logged in'}>
                      {u.last_login_at ? formatRelativeTime(u.last_login_at) : <span style={{ fontStyle: 'italic' }}>Never</span>}
                    </td>
                    <td>
                      <div style={{ display: 'flex', gap: '0.3rem' }}>
                        {u.role === 'funder' && u.funder_approved === false && (
                          <button className="mcl-action-btn" onClick={() => handleApproveFunder(u.user_id)} title="Approve Funder" data-testid={`approve-funder-${u.user_id}`} style={{ color: 'var(--mcl-success, #16a34a)' }}><ShieldCheck size={15} /></button>
                        )}
                        {u.role === 'lawyer' && u.lawyer_approved === false && (
                          <button className="mcl-action-btn" onClick={() => handleApproveLawyer(u.user_id)} title="Approve Lawyer" data-testid={`approve-lawyer-${u.user_id}`} style={{ color: 'var(--mcl-success, #16a34a)' }}><ShieldCheck size={15} /></button>
                        )}
                        {((u.role === 'funder' && u.funder_approved === true) || (u.role === 'lawyer' && u.lawyer_approved === true)) && (
                          <button className="mcl-action-btn" onClick={() => handleResendApproval(u.user_id, u.email)} title="Resend Approval Email" data-testid={`resend-approval-${u.user_id}`} style={{ color: 'var(--mcl-primary, #2563eb)' }}><Mail size={14} /></button>
                        )}
                        {u.role === 'growth_partner' && (
                          <button className="mcl-action-btn" onClick={() => setAssignmentsPartner(u)} title="Manage Lawyer Assignments" data-testid={`manage-assignments-${u.user_id}`} style={{ color: 'var(--mcl-primary, #2563eb)' }}><Users size={14} /></button>
                        )}
                        {u.role !== 'admin' && (
                          u.disabled ? (
                            <button className="mcl-action-btn" onClick={() => handleToggleDisabled(u)} title="Re-enable Account" data-testid={`enable-user-${u.user_id}`} style={{ color: 'var(--mcl-success, #16a34a)' }}><CheckCircle2 size={14} /></button>
                          ) : (
                            <button className="mcl-action-btn" onClick={() => handleToggleDisabled(u)} title="Disable Account" data-testid={`disable-user-${u.user_id}`} style={{ color: '#d97706' }}><Ban size={14} /></button>
                          )
                        )}
                        <button className="mcl-action-btn primary" onClick={() => navigate(`/admin/users/${u.user_id}/edit`)} data-testid={`edit-user-${u.user_id}`}><Pencil size={13} /></button>
                        <button className="mcl-action-btn" onClick={() => { setSelectedUser(u); setIsDeleteModalOpen(true); }} data-testid={`delete-user-${u.user_id}`} style={{ color: 'var(--mcl-danger)' }}><Trash2 size={13} /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>

      {/* Pagination */}
      {pagination.total_pages > 1 && (
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginTop: '1rem', flexWrap: 'wrap', gap: '0.75rem' }} data-testid="pagination">
          <span style={{ fontSize: '0.85rem', color: 'var(--mcl-text-muted)' }}>
            Showing {((pagination.page - 1) * pagination.limit) + 1} to {Math.min(pagination.page * pagination.limit, pagination.total)} of {pagination.total} users
          </span>
          <div style={{ display: 'flex', gap: '0.35rem' }}>
            <button className="mcl-btn mcl-btn-sm mcl-btn-outline" onClick={() => setPagination(prev => ({ ...prev, page: prev.page - 1 }))} disabled={!pagination.has_prev} data-testid="prev-page-btn"><ChevronLeft size={14} /> Prev</button>
            {Array.from({ length: Math.min(5, pagination.total_pages) }, (_, i) => {
              let pageNum;
              if (pagination.total_pages <= 5) pageNum = i + 1;
              else if (pagination.page <= 3) pageNum = i + 1;
              else if (pagination.page >= pagination.total_pages - 2) pageNum = pagination.total_pages - 4 + i;
              else pageNum = pagination.page - 2 + i;
              return (
                <button key={pageNum} className={`mcl-btn mcl-btn-sm ${pageNum === pagination.page ? 'mcl-btn-primary' : 'mcl-btn-outline'}`} onClick={() => setPagination(prev => ({ ...prev, page: pageNum }))} data-testid={`page-${pageNum}-btn`}>{pageNum}</button>
              );
            })}
            <button className="mcl-btn mcl-btn-sm mcl-btn-outline" onClick={() => setPagination(prev => ({ ...prev, page: prev.page + 1 }))} disabled={!pagination.has_next} data-testid="next-page-btn">Next <ChevronRight size={14} /></button>
          </div>
        </div>
      )}

      {/* Create User Modal */}
      <Dialog open={isCreateModalOpen} onOpenChange={setIsCreateModalOpen}>
        <DialogContent style={{ maxWidth: '960px', maxHeight: '85vh', display: 'flex', flexDirection: 'column' }}>
          <DialogHeader>
            <DialogTitle>Create New User</DialogTitle>
            <DialogDescription>Add a new user to the platform.</DialogDescription>
          </DialogHeader>
          <form onSubmit={handleCreateUser} style={{ display: 'contents' }}>
            <UserTypePicker
              role={formData.role}
              onChange={(role) => {
                // Switching type abandons any firm chosen for the previous one.
                setFormData({ ...formData, role, firm_id: '', new_firm_name: '' });
                setFirmMode('select'); setFirmSearch(''); setFirmOptions([]);
              }}
            />
            <div style={{ overflowY: 'auto', flex: 1, paddingRight: '0.5rem' }}>
              <UserFormFields
                formData={formData}
                setFormData={setFormData}
                formErrors={formErrors}
                firmMode={firmMode}
                setFirmMode={setFirmMode}
                firmSearch={firmSearch}
                searchFirms={searchFirms}
                firmOptions={firmOptions}
                onSelectFirm={(f) => {
                  setFormData({ ...formData, firm_id: f.firm_id, company_name: f.name });
                  setFirmSearch(f.name);
                  setFirmOptions([]);
                }}
              />
            </div>
            <DialogFooter style={{ paddingTop: '0.75rem', borderTop: '1px solid var(--mcl-border-light, #e2e8f0)' }}>
              <button type="button" className="mcl-btn mcl-btn-outline" onClick={() => setIsCreateModalOpen(false)}>Cancel</button>
              <button type="submit" className="mcl-btn mcl-btn-primary" disabled={submitting} data-testid="create-user-submit">
                {submitting ? 'Creating…' : 'Create & Send Invite'}
              </button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Delete User Confirmation */}
      <ConfirmDialog
        open={isDeleteModalOpen}
        onOpenChange={setIsDeleteModalOpen}
        tone="danger"
        title="Delete User"
        description={<>Permanently delete <strong>{selectedUser?.name || selectedUser?.email}</strong> and everything associated with the account.</>}
        warning="This cannot be undone."
        confirmLabel="Delete User"
        onConfirm={handleDeleteUser}
        testId="confirm-delete-user"
      />

      {/* Notify Funders Modal */}
      {showNotifyFundersModal && (
        <NotifyFundersModal
          onClose={() => setShowNotifyFundersModal(false)}
          funders={notifyFunders}
          loading={notifyLoading}
          selected={notifySelected}
          setSelected={setNotifySelected}
          message={notifyMessage}
          setMessage={setNotifyMessage}
          leadCount={notifyLeadCount}
          setLeadCount={setNotifyLeadCount}
          sending={notifySending}
          onLoad={async () => {
            setNotifyLoading(true);
            try {
              const data = await apiGet('/admin/users?role_filter=funder&limit=200');
              setNotifyFunders((data.users || []).filter(u => !u.disabled));
            } catch (e) { toast.error('Failed to load funders'); }
            finally { setNotifyLoading(false); }
          }}
          onSend={async () => {
            const ids = Object.keys(notifySelected).filter(k => notifySelected[k]);
            if (ids.length === 0) { toast.error('Select at least one funder'); return; }
            setNotifySending(true);
            try {
              const payload = { funder_ids: ids };
              if (notifyMessage.trim()) payload.custom_message = notifyMessage.trim();
              const lc = parseInt(notifyLeadCount, 10);
              if (lc > 0) payload.lead_count = lc;
              const data = await apiPost('/admin/notify-funders', payload);
              toast.success(`Notified ${data.sent_count} funder${data.sent_count === 1 ? '' : 's'}`);
              setShowNotifyFundersModal(false);
              setNotifySelected({}); setNotifyMessage(''); setNotifyLeadCount('');
            } catch (e) { toast.error(e.message); }
            finally { setNotifySending(false); }
          }}
        />
      )}

      {/* Growth Partner Assignments Modal */}
      {assignmentsPartner && (
        <GrowthPartnerAssignmentsModal
          partner={assignmentsPartner}
          onClose={() => setAssignmentsPartner(null)}
        />
      )}
    </div>
  );
};

/* ================================================================
   Law Firms Tab
   ================================================================ */
const LawFirmsTab = () => {
  const [firms, setFirms] = useState([]);
  const [total, setTotal] = useState(0);
  const [pages, setPages] = useState(1);
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [loading, setLoading] = useState(true);
  const [importing, setImporting] = useState(false);
  const [showDialog, setShowDialog] = useState(false);
  const [editingFirm, setEditingFirm] = useState(null);
  const [firmName, setFirmName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState(null);


  const fetchFirms = async (p = page, s = search) => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: p, limit: 25 });
      if (s) params.append('search', s);
      const data = await apiGet(`/admin/law-firms?${params}`);
      setFirms(data.firms); setTotal(data.total); setPages(data.pages); setPage(data.page);
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  };

  useEffect(() => { fetchFirms(1, search); }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    fetchFirms(1, search);
  };

  const handleImport = async () => {
    setImporting(true);
    try {
      const data = await apiPost('/admin/law-firms/import');
      toast.success(`Import complete: ${data.imported} new, ${data.updated} updated (${data.total} total)`);
      fetchFirms(1, search);
    } catch (error) { toast.error(error.message || 'Import failed'); }
    finally { setImporting(false); }
  };

  const handleSave = async () => {
    if (!firmName.trim()) { toast.error('Name is required'); return; }
    try {
      const path = editingFirm ? `/admin/law-firms/${editingFirm.firm_id}` : '/admin/law-firms';
      const body = { name: firmName.trim() };
      if (editingFirm) await apiPut(path, body); else await apiPost(path, body);
      toast.success(editingFirm ? 'Law firm updated' : 'Law firm added');
      setShowDialog(false); setEditingFirm(null); setFirmName('');
      fetchFirms(page, search);
    } catch (error) { toast.error(error.message || 'Failed to save'); }
  };

  const handleDelete = async (firmId) => {
    try {
      await apiDelete(`/admin/law-firms/${firmId}`);
      toast.success('Law firm deleted'); setDeleteConfirm(null); fetchFirms(page, search);
    } catch (error) { toast.error(error.message || 'Failed to delete'); }
  };

  const openAdd = () => { setEditingFirm(null); setFirmName(''); setShowDialog(true); };
  const openEdit = (firm) => { setEditingFirm(firm); setFirmName(firm.name); setShowDialog(true); };

  return (
    <div data-testid="law-firms-tab">
      <PageHeader title="Law Firms" subtitle={`${total} firms in database`} actions={
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button className="mcl-btn mcl-btn-outline" onClick={handleImport} disabled={importing} data-testid="import-law-firms-btn" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Upload size={16} /> {importing ? 'Importing...' : 'Import CSV'}
          </button>
          <button className="mcl-btn mcl-btn-primary" onClick={openAdd} data-testid="add-law-firm-btn" style={{ display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Plus size={16} /> Add Firm
          </button>
        </div>
      } />

      {/* Search */}
      <SectionCard>
        <form onSubmit={handleSearch} style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
          <div style={{ position: 'relative', flex: 1 }}>
            <Search size={16} style={{ position: 'absolute', left: 12, top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)' }} />
            <input className="mcl-form-input" style={{ paddingLeft: 36 }} placeholder="Search law firms..." value={search} onChange={(e) => setSearch(e.target.value)} data-testid="search-law-firms" />
          </div>
          <button type="submit" className="mcl-btn mcl-btn-primary" data-testid="search-law-firms-btn">Search</button>
        </form>

        {/* Table */}
        {loading ? <LoadingSpinner /> : firms.length === 0 ? (
          <EmptyState icon={Building2} title="No law firms found" message={search ? 'Try a different search term.' : 'Import the CSV or add firms manually.'} />
        ) : (
          <>
            <div className="mcl-table-container">
              <table className="mcl-table" data-testid="law-firms-table">
                <thead>
                  <tr>
                    <th style={{ width: '60%' }}>Firm Name</th>
                    <th>Added</th>
                    <th style={{ textAlign: 'right' }}>Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {firms.map((firm) => (
                    <tr key={firm.firm_id} data-testid={`firm-row-${firm.firm_id}`}>
                      <td style={{ fontWeight: 600 }}>{firm.name}</td>
                      <td style={{ color: 'var(--mcl-text-muted)', fontSize: '0.85rem' }}>{firm.created_at ? formatDate(firm.created_at) : '—'}</td>
                      <td style={{ textAlign: 'right' }}>
                        <div style={{ display: 'flex', gap: '0.25rem', justifyContent: 'flex-end' }}>
                          <button className="mcl-btn-icon" onClick={() => openEdit(firm)} title="Edit" data-testid={`edit-firm-${firm.firm_id}`}><Pencil size={15} /></button>
                          <button className="mcl-btn-icon" onClick={() => setDeleteConfirm(firm)} title="Delete" data-testid={`delete-firm-${firm.firm_id}`} style={{ color: 'var(--mcl-danger)' }}><Trash2 size={15} /></button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {/* Pagination */}
            {pages > 1 && (
              <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginTop: '1rem', padding: '0.75rem 0', borderTop: '1px solid var(--mcl-border-light)' }}>
                <span style={{ fontSize: '0.85rem', color: 'var(--mcl-text-muted)' }}>Page {page} of {pages} ({total} firms)</span>
                <div style={{ display: 'flex', gap: '0.5rem' }}>
                  <button className="mcl-btn mcl-btn-outline mcl-btn-sm" disabled={page <= 1} onClick={() => fetchFirms(page - 1, search)} data-testid="prev-page-btn"><ChevronLeft size={16} /> Prev</button>
                  <button className="mcl-btn mcl-btn-outline mcl-btn-sm" disabled={page >= pages} onClick={() => fetchFirms(page + 1, search)} data-testid="next-page-btn">Next <ChevronRight size={16} /></button>
                </div>
              </div>
            )}
          </>
        )}
      </SectionCard>

      {/* Add/Edit Dialog */}
      <Dialog open={showDialog} onOpenChange={setShowDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingFirm ? 'Edit Law Firm' : 'Add Law Firm'}</DialogTitle>
            <DialogDescription>{editingFirm ? 'Update the law firm name.' : 'Enter the name of the law firm to add.'}</DialogDescription>
          </DialogHeader>
          <div style={{ padding: '1rem 0' }}>
            <label className="mcl-form-label">Firm Name *</label>
            <input className="mcl-form-input" value={firmName} onChange={(e) => setFirmName(e.target.value)} placeholder="e.g. Morgan & Morgan" data-testid="input-firm-name" autoFocus onKeyDown={(e) => e.key === 'Enter' && handleSave()} />
          </div>
          <DialogFooter>
            <button className="mcl-btn mcl-btn-outline" onClick={() => setShowDialog(false)}>Cancel</button>
            <button className="mcl-btn mcl-btn-primary" onClick={handleSave} data-testid="save-firm-btn">{editingFirm ? 'Update' : 'Add Firm'}</button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation */}
      <ConfirmDialog
        open={!!deleteConfirm}
        onOpenChange={() => setDeleteConfirm(null)}
        tone="danger"
        title="Delete Law Firm"
        description={<>Permanently delete <strong>{deleteConfirm?.name}</strong>.</>}
        warning="This cannot be undone."
        confirmLabel="Delete Law Firm"
        onConfirm={() => handleDelete(deleteConfirm?.firm_id)}
        testId="confirm-delete-firm-btn"
      />
    </div>
  );
};

/* ================================================================
   Agreements Tab
   ================================================================ */
const AgreementsTab = () => {
  const [agreements, setAgreements] = useState([]);
  const [signatures, setSignatures] = useState({ funders: [], active_version: null });
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [viewTab, setViewTab] = useState('versions');
  const [newAgreement, setNewAgreement] = useState({ agreement_type: 'funder_non_circumvention', version_number: '', title: '', summary_of_changes: '', agreement_body: '' });


  useEffect(() => { fetchAgreements(); fetchSignatures(); }, []);

  const fetchAgreements = async () => {
    try {
      setAgreements(await apiGet(`/admin/agreements`));
    } catch (error) { console.error('Failed:', error); }
    finally { setLoading(false); }
  };

  const fetchSignatures = async () => {
    try {
      setSignatures(await apiGet(`/admin/agreements/signatures`));
    } catch (error) { console.error('Failed:', error); }
  };

  const handleCreate = async () => {
    if (!newAgreement.version_number || !newAgreement.title || !newAgreement.agreement_body) {
      toast.error('Please fill all required fields'); return;
    }
    try {
      await apiPost('/admin/agreements', newAgreement);
      toast.success('Agreement version created');
      setShowCreate(false);
      setNewAgreement({ agreement_type: 'funder_non_circumvention', version_number: '', title: '', summary_of_changes: '', agreement_body: '' });
      fetchAgreements();
    } catch (error) { toast.error(error.message || 'Failed to create agreement'); }
  };

  const handleActivate = async (agreementId, version) => {
    if (!window.confirm(`Activate version ${version}? All funders will need to re-sign.`)) return;
    try {
      await apiPost(`/admin/agreements/${agreementId}/activate`);
      toast.success(`Version ${version} activated`); fetchAgreements(); fetchSignatures();
    } catch (error) { toast.error(error.message || 'Failed to activate'); }
  };

  return (
    <div data-testid="agreements-tab">
      <PageHeader title="Agreement Management" subtitle="Manage funder agreements and view signature status" />

      {/* Sub-tabs */}
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.5rem' }}>
        <button className={`mcl-btn mcl-btn-sm ${viewTab === 'versions' ? 'mcl-btn-primary' : 'mcl-btn-outline'}`} onClick={() => setViewTab('versions')} data-testid="tab-versions">Versions</button>
        <button className={`mcl-btn mcl-btn-sm ${viewTab === 'signatures' ? 'mcl-btn-primary' : 'mcl-btn-outline'}`} onClick={() => setViewTab('signatures')} data-testid="tab-signatures">Funder Signatures</button>
      </div>

      {viewTab === 'versions' && (
        <>
          <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: '1rem' }}>
            <button className="mcl-btn mcl-btn-primary mcl-btn-sm" onClick={() => setShowCreate(!showCreate)} data-testid="create-agreement-btn">
              {showCreate ? 'Cancel' : '+ New Version'}
            </button>
          </div>

          {showCreate && (
            <SectionCard title="Create New Agreement Version" className="mb-3">
              <div className="mcl-form-row">
                <div className="mcl-form-group">
                  <label className="mcl-form-label">Version Number *</label>
                  <input className="mcl-form-input" value={newAgreement.version_number} onChange={(e) => setNewAgreement(p => ({ ...p, version_number: e.target.value }))} placeholder="e.g. 2.0" data-testid="input-agreement-version" />
                </div>
                <div className="mcl-form-group">
                  <label className="mcl-form-label">Title *</label>
                  <input className="mcl-form-input" value={newAgreement.title} onChange={(e) => setNewAgreement(p => ({ ...p, title: e.target.value }))} placeholder="Agreement title" data-testid="input-agreement-title" />
                </div>
              </div>
              <div className="mcl-form-group">
                <label className="mcl-form-label">Summary of Changes</label>
                <input className="mcl-form-input" value={newAgreement.summary_of_changes} onChange={(e) => setNewAgreement(p => ({ ...p, summary_of_changes: e.target.value }))} placeholder="Brief summary of what changed" data-testid="input-agreement-summary" />
              </div>
              <div className="mcl-form-group">
                <label className="mcl-form-label">Agreement Body (HTML) *</label>
                <textarea className="mcl-form-textarea" rows={10} value={newAgreement.agreement_body} onChange={(e) => setNewAgreement(p => ({ ...p, agreement_body: e.target.value }))} placeholder="Full agreement text (HTML supported)" data-testid="input-agreement-body" />
              </div>
              <button className="mcl-btn mcl-btn-primary" onClick={handleCreate} data-testid="submit-agreement-btn">Create Version</button>
            </SectionCard>
          )}

          <SectionCard>
            {loading ? <LoadingSpinner /> : agreements.length === 0 ? <EmptyState title="No agreements" message="Create the first agreement version" /> : (
              <div className="mcl-table-container">
                <table className="mcl-table">
                  <thead><tr>
                    <th>Version</th><th>Title</th><th>Status</th><th>Signatures</th><th>Created</th><th>Actions</th>
                  </tr></thead>
                  <tbody>
                    {agreements.map((a) => (
                      <tr key={a.agreement_id}>
                        <td><strong>{a.version_number}</strong></td>
                        <td style={{ maxWidth: 300 }}>{a.title}</td>
                        <td>
                          {a.is_active ? (
                            <span className="mcl-badge mcl-badge-success" style={{ fontSize: '0.7rem' }}>Active</span>
                          ) : (
                            <span className="mcl-badge mcl-badge-muted" style={{ fontSize: '0.7rem' }}>Inactive</span>
                          )}
                        </td>
                        <td>{a.signature_count || 0}</td>
                        <td style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{formatDate(a.created_at)}</td>
                        <td>
                          {!a.is_active && (
                            <button className="mcl-btn mcl-btn-sm mcl-btn-success" onClick={() => handleActivate(a.agreement_id, a.version_number)} data-testid={`activate-agreement-${a.agreement_id}`} style={{ fontSize: '0.75rem' }}>
                              Activate
                            </button>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </SectionCard>
        </>
      )}

      {viewTab === 'signatures' && (
        <SectionCard title={`Funder Signatures ${signatures.active_version ? `(Active: v${signatures.active_version})` : ''}`}>
          {signatures.funders.length === 0 ? <EmptyState title="No funders" message="No funder accounts found" /> : (
            <div className="mcl-table-container">
              <table className="mcl-table">
                <thead><tr>
                  <th>Funder</th><th>Company</th><th>Status</th><th>Version Signed</th><th>Signed Name</th><th>Signed At</th>
                </tr></thead>
                <tbody>
                  {signatures.funders.map((f) => (
                    <tr key={f.user_id}>
                      <td>
                        <div style={{ fontWeight: 500 }}>{f.name}</div>
                        <div style={{ fontSize: '0.75rem', color: 'var(--mcl-text-muted)' }}>{f.email}</div>
                      </td>
                      <td>{f.company_name || '-'}</td>
                      <td>
                        {f.signed_current ? (
                          <span className="mcl-badge mcl-badge-success" style={{ fontSize: '0.7rem' }}>Signed (Current)</span>
                        ) : f.signed ? (
                          <span className="mcl-badge mcl-badge-warning" style={{ fontSize: '0.7rem' }}>Signed (Outdated)</span>
                        ) : (
                          <span className="mcl-badge mcl-badge-danger" style={{ fontSize: '0.7rem' }}>Not Signed</span>
                        )}
                      </td>
                      <td>{f.signed_version || '-'}</td>
                      <td style={{ fontFamily: "'Georgia', serif", fontStyle: 'italic' }}>{f.signed_name || '-'}</td>
                      <td style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{f.signed_at ? formatDate(f.signed_at) : '-'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </SectionCard>
      )}
    </div>
  );
};

export default AdminDashboard;

/* ================================================================
   Email Log Tab — admin observability for outbound email attempts.
   Backed by GET /admin/email-log with optional outcome + recipient filters.
   ================================================================ */
/* Partial /apply applications — people who started and never finished.
 *
 * Converted leads are never listed. The backend enforces that in the query
 * rather than accepting a filter flag, because the whole point of this view
 * is a contact list: showing somebody who already has an account and a filed
 * application would produce outreach that is worse than none. Their count is
 * still shown, so the funnel is visible without the rows being actionable. */
const LeadsTab = () => {
  const [leads, setLeads] = useState([]);
  const [totals, setTotals] = useState({ total: 0, converted: 0, pages: 1 });
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const [query, setQuery] = useState('');
  const [loading, setLoading] = useState(true);

  const fetchLeads = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ page: String(page), limit: '25' });
      if (query.trim()) params.set('search', query.trim());
      const data = await apiGet(`/admin/leads?${params.toString()}`);
      setLeads(data.leads || []);
      setTotals({ total: data.total || 0, converted: data.converted || 0, pages: data.pages || 1 });
    } catch (e) {
      toast.error('Failed to load leads');
    }
    setLoading(false);
  };

  // Refetch on page/search only. fetchLeads is redefined every render, so
  // listing it would refetch in a loop; the manual Refresh button covers the
  // case where the caller wants a re-read without changing either.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchLeads(); }, [page, query]);

  const onSearch = (e) => { e.preventDefault(); setPage(1); setQuery(search); };

  const money = (n) => (typeof n === 'number' ? `$${n.toLocaleString('en-US')}` : '-');
  // furthest_step is stored 0-indexed, matching the wizard's own state.
  const stepLabel = (n) => `Step ${(typeof n === 'number' ? n : 0) + 1} of 3`;

  return (
    <div data-testid="leads-tab">
      <PageHeader
        title="Leads"
        subtitle="Applications that were started but never finished. Converted leads are excluded — those people already have an account."
        actions={<button className="mcl-btn mcl-btn-ghost" onClick={fetchLeads} data-testid="leads-refresh"><RefreshCcw size={14} /> Refresh</button>}
      />

      <div className="row g-3 mb-3">
        <div className="col-md-6">
          <SummaryStatCard icon={UserPlus} label="Open leads" value={totals.total} />
        </div>
        <div className="col-md-6">
          <SummaryStatCard icon={CheckCircle2} label="Converted" value={totals.converted} variant="success" />
        </div>
      </div>

      <SectionCard className="mb-3">
        <form onSubmit={onSearch} style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px', minWidth: '220px' }}>
            <label className="mcl-form-label">Search</label>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)' }} />
              <input
                type="text"
                className="mcl-form-input"
                placeholder="Email, name, phone, attorney or firm"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                style={{ paddingLeft: '32px' }}
                data-testid="leads-search-input"
              />
            </div>
          </div>
          <button type="submit" className="mcl-btn mcl-btn-primary" data-testid="leads-search-btn">Search</button>
        </form>
      </SectionCard>

      <SectionCard>
        {loading ? (
          <LoadingSpinner />
        ) : leads.length === 0 ? (
          <EmptyState
            icon={UserPlus}
            title="No open leads"
            text={query ? 'No unfinished applications match that search.' : 'Every application that was started has been completed.'}
          />
        ) : (
          <div className="table-responsive">
            <table className="mcl-table" data-testid="leads-table">
              <thead>
                <tr>
                  <th>Contact</th>
                  <th>Case</th>
                  <th>Amount</th>
                  <th>Attorney</th>
                  <th>Reached</th>
                  <th>Last activity</th>
                </tr>
              </thead>
              <tbody>
                {leads.map((lead) => (
                  <tr key={lead.email} data-testid={`lead-row-${lead.email}`}>
                    <td>
                      <div style={{ fontWeight: 600 }}>
                        {[lead.firstName, lead.lastName].filter(Boolean).join(' ') || '(no name yet)'}
                      </div>
                      <div style={{ fontSize: '0.8rem' }}>
                        <a href={`mailto:${lead.email}`}>{lead.email}</a>
                      </div>
                      {lead.phone && <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{lead.phone}</div>}
                    </td>
                    <td>
                      <div>{lead.caseType ? getCaseTypeLabel(lead.caseType) : '-'}</div>
                      {lead.accidentState && <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{lead.accidentState}</div>}
                    </td>
                    <td>{money(lead.requestedAmount)}</td>
                    <td>
                      <div>{lead.attorneyName || '-'}</div>
                      {lead.lawFirmName && <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>{lead.lawFirmName}</div>}
                    </td>
                    <td><span className="mcl-badge" style={{ fontSize: '0.7rem' }}>{stepLabel(lead.furthest_step)}</span></td>
                    <td>
                      <div>{formatRelativeTime(lead.updated_at)}</div>
                      <div style={{ fontSize: '0.8rem', color: 'var(--mcl-text-muted)' }}>Started {formatDate(lead.created_at)}</div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}

        {totals.pages > 1 && (
          <div style={{ display: 'flex', gap: '0.4rem', justifyContent: 'center', marginTop: '1rem' }}>
            <button className="mcl-btn mcl-btn-sm mcl-btn-outline" onClick={() => setPage((p) => p - 1)} disabled={page <= 1} data-testid="leads-prev-page"><ChevronLeft size={14} /> Prev</button>
            <span style={{ alignSelf: 'center', fontSize: '0.85rem', color: 'var(--mcl-text-muted)' }}>Page {page} of {totals.pages}</span>
            <button className="mcl-btn mcl-btn-sm mcl-btn-outline" onClick={() => setPage((p) => p + 1)} disabled={page >= totals.pages} data-testid="leads-next-page">Next <ChevronRight size={14} /></button>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

const EmailLogTab = () => {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [recipient, setRecipient] = useState('');
  const [outcome, setOutcome] = useState('all');
  const [limit, setLimit] = useState(50);


  const fetchEntries = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('limit', String(limit));
      if (outcome !== 'all') params.set('outcome', outcome);
      if (recipient.trim()) params.set('recipient', recipient.trim());
      const data = await apiGet(`/admin/email-log?${params.toString()}`);
      setEntries(data.entries || []);
    } catch (e) {
      toast.error('Failed to load email log');
    }
    setLoading(false);
  };

  useEffect(() => { fetchEntries(); }, [outcome, limit]);

  const onSearch = (e) => { e.preventDefault(); fetchEntries(); };

  const outcomeBadge = (o) => {
    if (o === 'sent') return <span className="mcl-badge mcl-badge-success" style={{ fontSize: '0.7rem' }}><CheckCircle size={10} style={{ marginRight: '3px' }} />Sent</span>;
    if (o === 'failed') return <span className="mcl-badge mcl-badge-danger" style={{ fontSize: '0.7rem' }}><AlertTriangle size={10} style={{ marginRight: '3px' }} />Failed</span>;
    if (o === 'skipped_dev_guard') return <span className="mcl-badge mcl-badge-warning" style={{ fontSize: '0.7rem' }}><Ban size={10} style={{ marginRight: '3px' }} />Dev-skipped</span>;
    return <span className="mcl-badge" style={{ fontSize: '0.7rem' }}>{o}</span>;
  };

  const formatTimestamp = (iso) => formatDateTime(iso) || '-';

  return (
    <div data-testid="email-log-tab">
      <PageHeader title="Outbound Email Log" subtitle="Forensic record of every send attempt. Use this when a user reports a missing notification." actions={
        <button className="mcl-btn mcl-btn-ghost" onClick={fetchEntries} data-testid="email-log-refresh"><RefreshCcw size={14} /> Refresh</button>
      } />
      <SectionCard className="mb-3">
        <form onSubmit={onSearch} style={{ display: 'flex', gap: '0.6rem', alignItems: 'flex-end', flexWrap: 'wrap' }}>
          <div style={{ flex: '1 1 260px', minWidth: '220px' }}>
            <label className="mcl-form-label">Recipient (substring)</label>
            <div style={{ position: 'relative' }}>
              <Search size={14} style={{ position: 'absolute', left: '10px', top: '50%', transform: 'translateY(-50%)', color: 'var(--mcl-text-muted)' }} />
              <input
                type="text"
                className="mcl-form-input"
                placeholder="e.g. lawyer@firm.com"
                value={recipient}
                onChange={(e) => setRecipient(e.target.value)}
                style={{ paddingLeft: '32px' }}
                data-testid="email-log-recipient-input"
              />
            </div>
          </div>
          <div style={{ minWidth: '160px' }}>
            <label className="mcl-form-label">Outcome</label>
            <select className="mcl-form-input" value={outcome} onChange={(e) => setOutcome(e.target.value)} data-testid="email-log-outcome-select">
              <option value="all">All</option>
              <option value="sent">Sent</option>
              <option value="failed">Failed</option>
              <option value="skipped_dev_guard">Dev-skipped</option>
            </select>
          </div>
          <div style={{ minWidth: '110px' }}>
            <label className="mcl-form-label">Limit</label>
            <select className="mcl-form-input" value={limit} onChange={(e) => setLimit(parseInt(e.target.value, 10))} data-testid="email-log-limit-select">
              <option value={25}>25</option>
              <option value={50}>50</option>
              <option value={100}>100</option>
              <option value={250}>250</option>
            </select>
          </div>
          <button type="submit" className="mcl-btn mcl-btn-primary" data-testid="email-log-search-btn"><Search size={14} /> Search</button>
        </form>
      </SectionCard>

      <SectionCard>
        {loading ? <LoadingSpinner /> : entries.length === 0 ? (
          <EmptyState
            icon={Inbox}
            title="No email attempts found"
            description={recipient || outcome !== 'all' ? 'Try widening the filters above.' : 'Email attempts will appear here as the platform sends notifications.'}
          />
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="mcl-table" data-testid="email-log-table">
              <thead>
                <tr>
                  <th style={{ width: '170px' }}>Sent At</th>
                  <th>Recipient(s)</th>
                  <th>Subject</th>
                  <th style={{ width: '120px' }}>Outcome</th>
                  <th style={{ width: '240px' }}>Resend ID / Error</th>
                </tr>
              </thead>
              <tbody>
                {entries.map((entry, idx) => (
                  <tr key={`${entry.resend_id || ''}_${entry.sent_at}_${idx}`} data-testid={`email-log-row-${idx}`}>
                    <td style={{ fontSize: '0.82rem', whiteSpace: 'nowrap', color: 'var(--mcl-text-muted)' }}>{formatTimestamp(entry.sent_at)}</td>
                    <td style={{ fontSize: '0.85rem' }}>
                      <div style={{ fontWeight: 500, color: 'var(--mcl-text-heading)' }}>{(entry.to || []).join(', ') || '-'}</div>
                      {entry.cc && entry.cc.length > 0 && <div style={{ fontSize: '0.72rem', color: 'var(--mcl-text-muted)' }}>cc: {entry.cc.join(', ')}</div>}
                    </td>
                    <td style={{ fontSize: '0.85rem' }}>{entry.subject || <span style={{ color: 'var(--mcl-text-light)' }}>-</span>}</td>
                    <td>{outcomeBadge(entry.outcome)}</td>
                    <td style={{ fontSize: '0.78rem', fontFamily: 'monospace', color: entry.error ? 'var(--mcl-danger)' : 'var(--mcl-text-muted)', wordBreak: 'break-all' }}>
                      {entry.error ? entry.error : entry.resend_id || <span style={{ color: 'var(--mcl-text-light)' }}>-</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </SectionCard>
    </div>
  );
};

/* ================================================================
   Platform Settings Tab — tier-based MyCaseLoan Platform Fee config
   ================================================================ */
const PlatformSettingsTab = () => {
  const [tiers, setTiers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);


  const fetchTiers = async () => {
    setLoading(true);
    try {
      const data = await apiGet('/admin/platform-fee-tiers');
      setTiers((data.tiers || []).map(t => ({
        min_amount: t.min_amount,
        max_amount: t.max_amount,
        fee_amount: t.fee_amount,
      })));
    } catch (e) {
      toast.error(e.message || 'Failed to load tiers');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchTiers(); }, []);

  const updateTier = (idx, field, value) => {
    setTiers(prev => prev.map((t, i) => {
      if (i !== idx) return t;
      const next = { ...t };
      if (field === 'max_amount' && (value === '' || value === null)) next.max_amount = null;
      else next[field] = value === '' ? '' : Number(value);
      return next;
    }));
  };

  const addTier = () => {
    setTiers(prev => {
      const last = prev[prev.length - 1];
      // Convert previous "unlimited" tier into a bounded tier; new tier becomes unlimited.
      const newPrevMax = last && last.max_amount === null ? (Number(last.min_amount) + 5000) : last?.max_amount;
      const updated = prev.map((t, i) => i === prev.length - 1 ? { ...t, max_amount: newPrevMax } : t);
      const nextMin = newPrevMax !== null && newPrevMax !== undefined ? Number(newPrevMax) + 1 : 0;
      return [...updated, { min_amount: nextMin, max_amount: null, fee_amount: 0 }];
    });
  };

  const removeTier = (idx) => {
    setTiers(prev => {
      if (prev.length <= 1) {
        toast.error('At least one tier is required.');
        return prev;
      }
      const removed = prev.filter((_, i) => i !== idx);
      // Last tier must always be unlimited — re-open the new last tier if needed.
      if (removed.length > 0) {
        removed[removed.length - 1] = { ...removed[removed.length - 1], max_amount: null };
      }
      return removed;
    });
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        tiers: tiers.map(t => ({
          min_amount: Number(t.min_amount) || 0,
          max_amount: t.max_amount === null || t.max_amount === '' ? null : Number(t.max_amount),
          fee_amount: Number(t.fee_amount) || 0,
        })),
      };
      const data = await apiPut('/admin/platform-fee-tiers', payload);
      toast.success('Platform fee tiers updated');
      setTiers((data.tiers || []).map(t => ({
        min_amount: t.min_amount,
        max_amount: t.max_amount,
        fee_amount: t.fee_amount,
      })));
    } catch (e) {
      toast.error(e.message || 'Failed to save tiers');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <LoadingSpinner />;

  return (
    <div data-testid="platform-settings-tab">
      <PageHeader title="Platform Settings" subtitle="Configure platform-wide pricing and behaviour" />
      <SectionCard
        title="MyCaseLoan Platform Fee — Tier Schedule"
        description="A flat fee added to every funded offer, determined by the advance amount. Tiers must start at $0, be contiguous (no gaps or overlaps), and the last tier must have an unlimited upper bound."
      >
        <div style={{ overflowX: 'auto' }}>
          <table className="mcl-table" data-testid="platform-fee-tiers-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <th style={{ textAlign: 'left', padding: '0.6rem 0.5rem', borderBottom: '1px solid var(--mcl-border)', fontSize: '0.78rem', color: 'var(--mcl-text-muted)' }}>Tier</th>
                <th style={{ textAlign: 'left', padding: '0.6rem 0.5rem', borderBottom: '1px solid var(--mcl-border)', fontSize: '0.78rem', color: 'var(--mcl-text-muted)' }}>Min Advance ($)</th>
                <th style={{ textAlign: 'left', padding: '0.6rem 0.5rem', borderBottom: '1px solid var(--mcl-border)', fontSize: '0.78rem', color: 'var(--mcl-text-muted)' }}>Max Advance ($)</th>
                <th style={{ textAlign: 'left', padding: '0.6rem 0.5rem', borderBottom: '1px solid var(--mcl-border)', fontSize: '0.78rem', color: 'var(--mcl-text-muted)' }}>Platform Fee ($)</th>
                <th style={{ textAlign: 'right', padding: '0.6rem 0.5rem', borderBottom: '1px solid var(--mcl-border)' }}></th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((t, idx) => {
                const isLast = idx === tiers.length - 1;
                return (
                  <tr key={idx} data-testid={`platform-fee-tier-row-${idx}`}>
                    <td style={{ padding: '0.55rem 0.5rem', borderBottom: '1px solid var(--mcl-border-light)', fontSize: '0.85rem', color: 'var(--mcl-text-muted)' }}>{idx + 1}</td>
                    <td style={{ padding: '0.55rem 0.5rem', borderBottom: '1px solid var(--mcl-border-light)' }}>
                      <input
                        type="number"
                        className="mcl-form-input"
                        value={t.min_amount}
                        onChange={(e) => updateTier(idx, 'min_amount', e.target.value)}
                        data-testid={`tier-min-${idx}`}
                        style={{ maxWidth: '160px' }}
                      />
                    </td>
                    <td style={{ padding: '0.55rem 0.5rem', borderBottom: '1px solid var(--mcl-border-light)' }}>
                      {isLast ? (
                        <span data-testid={`tier-max-unlimited-${idx}`} style={{ fontSize: '0.85rem', color: 'var(--mcl-text-muted)', fontStyle: 'italic' }}>Unlimited</span>
                      ) : (
                        <input
                          type="number"
                          className="mcl-form-input"
                          value={t.max_amount === null ? '' : t.max_amount}
                          onChange={(e) => updateTier(idx, 'max_amount', e.target.value)}
                          data-testid={`tier-max-${idx}`}
                          style={{ maxWidth: '160px' }}
                        />
                      )}
                    </td>
                    <td style={{ padding: '0.55rem 0.5rem', borderBottom: '1px solid var(--mcl-border-light)' }}>
                      <input
                        type="number"
                        className="mcl-form-input"
                        value={t.fee_amount}
                        onChange={(e) => updateTier(idx, 'fee_amount', e.target.value)}
                        data-testid={`tier-fee-${idx}`}
                        style={{ maxWidth: '140px' }}
                      />
                    </td>
                    <td style={{ padding: '0.55rem 0.5rem', borderBottom: '1px solid var(--mcl-border-light)', textAlign: 'right' }}>
                      <button
                        type="button"
                        className="mcl-btn mcl-btn-sm mcl-btn-ghost"
                        onClick={() => removeTier(idx)}
                        disabled={tiers.length <= 1}
                        data-testid={`tier-remove-${idx}`}
                        aria-label="Remove tier"
                        title="Remove this tier"
                        style={{ color: 'var(--mcl-danger)' }}
                      >
                        <Trash2 size={14} />
                      </button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <div style={{ display: 'flex', gap: '0.6rem', marginTop: '1rem', flexWrap: 'wrap' }}>
          <button type="button" className="mcl-btn mcl-btn-outline" onClick={addTier} data-testid="add-tier-btn">
            <Plus size={14} /> Add Tier
          </button>
          <button type="button" className="mcl-btn mcl-btn-primary" onClick={handleSave} disabled={saving} data-testid="save-tiers-btn">
            <Save size={14} /> {saving ? 'Saving...' : 'Save Tiers'}
          </button>
        </div>

        <div style={{ marginTop: '1rem', padding: '0.75rem 0.9rem', background: '#f8fafc', border: '1px solid var(--mcl-border-light)', borderRadius: '8px', fontSize: '0.78rem', color: 'var(--mcl-text-muted)', lineHeight: 1.5 }}>
          <strong style={{ color: 'var(--mcl-text-body)' }}>Rules:</strong> Tier 1 must start at $0. Each subsequent tier must start at <code>prev.max + 1</code>. Only the last tier may have an unlimited upper bound (shown as &quot;Unlimited&quot;). Changes apply to new offers immediately; previously-saved offers retain their stored platform fee.
        </div>
      </SectionCard>
    </div>
  );
};

